import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { api, fmtMoney } from '../lib/api.js';

export default function Equipment() {
  const { isOwner, canManageEquipment } = useOutletContext();
  const [list, setList] = useState(null);
  const [err, setErr] = useState('');

  const [assigning, setAssigning] = useState(false);

  function load() {
    return api.get('/api/equipment').then(({ equipment }) => setList(equipment)).catch(e => setErr(e.message));
  }
  useEffect(() => { load(); }, []);

  if (err) return <div className="text-rust-700 text-sm">{err}</div>;
  if (!list) return <div className="text-ink-700/60 italic">Loading…</div>;

  // Group by parent (top-level vs implements)
  const tops = list.filter(e => e.parent_id == null);
  const childrenOf = (id) => list.filter(e => e.parent_id === id);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-serif text-olive-900">Equipment</h1>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Link to="/calendar" className="btn-ghost text-sm">Service calendar</Link>
          {canManageEquipment && list.length > 0 && (
            <button onClick={() => setAssigning(a => !a)} className="btn-ghost text-sm">Assign all…</button>
          )}
          {canManageEquipment && <Link to="/equipment/new" className="btn-primary">+ Add</Link>}
        </div>
      </header>

      {assigning && <AssignAll count={list.length} onClose={() => setAssigning(false)} onDone={load} />}

      {tops.length === 0 ? (
        <div className="card text-center py-10">
          <p className="text-ink-700/70">No equipment yet.</p>
          {canManageEquipment && (
            <Link to="/equipment/new" className="btn-primary mt-4">Add your first machine</Link>
          )}
        </div>
      ) : (
        <ul className="space-y-3">
          {tops.map(e => (
            <li key={e.id} className="card">
              <Link to={`/equipment/${e.id}`} className="block">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="font-medium text-ink-900">{e.year ? `${e.year} ` : ''}{e.make} {e.model}</div>
                    <div className="text-sm text-ink-700/70 mt-0.5">
                      {e.category && <span>{e.category.replace('_', ' ')} · </span>}
                      {e.serial && <span>SN {e.serial} · </span>}
                      {e.hour_meter != null && <span>{e.hour_meter}hr</span>}
                    </div>
                    <div className="text-xs text-ink-700/60 mt-1">
                      Maintainer: <span className="font-medium">{e.primary_maintainer_name || '—'}</span>
                      {e.backup_maintainer_name && <span className="text-ink-700/50"> (bk: {e.backup_maintainer_name})</span>}
                    </div>
                    {isOwner && e.purchase_price_cents != null && (
                      <div className="text-xs text-ink-700/50 mt-0.5">Purchase: {fmtMoney(e.purchase_price_cents)}</div>
                    )}
                  </div>
                  <div className="text-ink-700/40">›</div>
                </div>
              </Link>
              {childrenOf(e.id).length > 0 && (
                <ul className="mt-3 pl-4 border-l-2 border-olive-100 space-y-1">
                  {childrenOf(e.id).map(c => (
                    <li key={c.id}>
                      <Link to={`/equipment/${c.id}`} className="text-sm text-olive-700 hover:underline">
                        {c.make} {c.model} <span className="text-ink-700/50">({c.category})</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Make one person the primary maintainer of every machine (and the default for new ones).
function AssignAll({ count, onClose, onDone }) {
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState('');
  const [setDefault, setSetDefault] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => { api.get('/api/users').then(({ users }) => setUsers(users)).catch(e => setErr(e.message)); }, []);

  async function apply() {
    const u = users.find(x => String(x.id) === String(userId));
    if (!u) return;
    if (!confirm(`Make ${u.name} the primary maintainer of all ${count} machine${count === 1 ? '' : 's'}?\n\nService alerts and My Day tasks for every machine will go to ${u.name}.`)) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await api.post('/api/equipment/assign-all', { user_id: u.id, set_default: setDefault });
      setMsg(`✓ ${r.user} now maintains all ${r.total} machine${r.total === 1 ? '' : 's'} (${r.changed} changed)${r.default_set ? ', and new equipment will default to them' : ''}.`);
      await onDone();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <section className="card space-y-3">
      <div>
        <h2 className="font-medium text-olive-900">Assign all equipment to one person</h2>
        <p className="text-sm text-ink-700/70">Sets the <strong>primary maintainer</strong> on every machine. Backup maintainers stay as they are.</p>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-end gap-2">
        <div className="flex-1">
          <label className="label">Primary maintainer</label>
          <select className="input" value={userId} onChange={e => setUserId(e.target.value)}>
            <option value="">— choose a person —</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <button onClick={apply} disabled={!userId || busy} className="btn-primary">{busy ? 'Assigning…' : 'Assign all'}</button>
        <button onClick={onClose} className="btn-ghost">Close</button>
      </div>
      <label className="flex items-center gap-2 text-sm text-ink-900">
        <input type="checkbox" checked={setDefault} onChange={e => setSetDefault(e.target.checked)} />
        Also assign equipment added later to this person
      </label>
      {msg && <div className="rounded-lg bg-olive-100 text-olive-900 p-2 text-sm">{msg}</div>}
      {err && <div className="text-rust-700 text-sm">{err}</div>}
    </section>
  );
}
